/*
 * Copyright (c) 2020, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import React from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import Link from "@material-ui/core/Link";
import Typography from "@material-ui/core/Typography";

type FooterLinkItem = {
  text: string;
  url: string;
};

const useStyles = makeStyles((theme) => ({
  footer: {
    top: "auto",
    bottom: 0,
    minWidth: "100%",
    padding: theme.spacing(2, 1),
    "& .footer-link:hover": {
      color: theme.palette.common.white,
    },
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    zIndex: 1300,
    ...theme.custom.footer.styles,
  },
  loginFooter: {
    position: "sticky",
    left: 0,
    bottom: 0,
    height: theme.spacing(6),
    width: "100%",
    display: "flex",
    alignItems: "center",
    borderTop: "1px solid",
    borderTopColor: "rgba(228,232,238,0.5)",
    color: "#40404B",
    backgroundColor: "#F7F8FB",
    "& .footer-link:hover": {
      color: theme.palette.grey,
    },
    ...theme.custom.footer.loginFooter,
  },
  copyright: {
    textDecoration: "inherit",
    "&:hover": {
      color: "#ff7300",
    },
    transition: "color 0.3s linear",
  },
  wso2Logo: {
    height: theme.spacing(2),
    width: theme.spacing(5.211),
    cursor: "pointer",
    marginLeft: theme.spacing(0.75),
    marginTop: theme.spacing(2),
  },
}));

function DevPortalFooter() {
  const classes = useStyles();
  const defaultUrl = "https://wso2.com/";
  const location = useLocation();
  const path = location.pathname;
  const theme = useTheme();
  return (
    <footer
      className={
        path.startsWith("/login") ? classes.loginFooter : classes.footer
      }
    >
      <Box width="100%">
        <Grid spacing={1} container justifyContent="space-between" alignItems="center">
          <Grid item xs={4}>
            <Box flexDirection="row" display="flex" fontSize="body2.fontSize">
              {theme.custom.footer.items.map(
                ({ text, url }: FooterLinkItem) => (
                  <Box key={text} mx={2} display="flex">
                    <Link
                      underline="none"
                      component={RouterLink}
                      target="_blank"
                      rel="noopener"
                      to={{ pathname: url }}
                      color="textSecondary"
                    >
                      {text}
                    </Link>
                  </Box>
                )
              )}
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Box display="flex" alignItems="center" justifyContent="flex-end">
              <Box mr={2} color="text.secondary">
                {!theme.custom.footer.logo.src ? (
                  <Typography
                    className="choreo-footer-copyright"
                    display="inline"
                    variant="body2"
                  >
                    &copy;&nbsp; 2025,{" "}
                    <span className={classes.copyright}>WSO2 LLC.</span>
                  </Typography>
                ) : (
                  <img
                    alt={theme.custom.footer.logo.alt}
                    src={theme.custom.footer.logo.src}
                    className={classes.wso2Logo}
                  />
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </footer>
  );
}

export default DevPortalFooter;
